import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AnalyticsLogin,
  FtSignInInterface,
  SignInInterface,
} from '@shared/sign-in/sign-in.interface';
import { SignInService } from '@services/sign-in.service';
import { GlobalConfigService } from '@services/global-config-service';
import { LanguageSelector, SignInEvent } from '@frk/eds-components';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Logger } from '@utils/logger';
import { Observable, Subject } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@shared/translate/translate.service';
import { Page } from '@bloomreach/spa-sdk';
import { GatewayModalService } from '@services/gateway-modal.service';
import { SegmentService } from '@services/segment.service';
import { AppStateService } from '@services/app-state.service';
import { Router } from '@angular/router';
import { LanguageSelectorService } from '@services/language-selector.service';
const logger = Logger.getLogger('SignInModalComponent');

@Component({
  selector: 'ft-sign-in-modal',
  templateUrl: './sign-in-modal.component.html',
  styleUrls: ['./sign-in-modal.component.scss'],
})
export class SignInModalComponent implements OnInit, OnDestroy {
  @Input() page: Page;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private isGatewaySignIn = false;
  public isVisible$: Observable<boolean>;
  public signInForm: FormGroup;
  public isSubmitButtonDisabled = false;
  public signInComponentContent: FtSignInInterface;
  public useSmallText: true;
  public isLegacy: boolean;
  public showClose = true;
  public languageOptions: LanguageSelector;

  constructor(
    private appStateService: AppStateService,
    private formBuilder: FormBuilder,
    private gatewayModalService: GatewayModalService,
    private globalConfigService: GlobalConfigService,
    private segmentService: SegmentService,
    private signInService: SignInService,
    private translateService: TranslateService,
    private languageSelectorService: LanguageSelectorService,
    private router: Router,
    @Inject(DOCUMENT) readonly documentRef: Document
  ) {
    this.signInForm = this.signInService.signInFormInit(this.formBuilder);
    this.signInComponentContent = this.signInService.setDefaultComponent();
    this.isLegacy = this.getLegacy();
  }

  ngOnInit(): void {
    this.isGatewaySignIn = this.segmentService.checkSegmentRestrict(
      this.page,
      true
    );
    this.signInService.setComponentParams(
      this.getSignIn(),
      this.signInComponentContent
    );
    // Set ctaText to null to not display sign in button for EDS sign-in component.
    this.signInComponentContent.ctaText = null;
    // Set introText from label
    this.signInComponentContent.introText = this.translateService.instant(
      'signin.introText'
    );
    this.signInService
      .setSignInFavoriteModal$()
      .subscribe((showMsg: boolean) => {
        if (showMsg) {
          this.signInComponentContent.introText = this.translateService.instant(
            'products.signin-modal-fav-fund-msg'
          );
        }
      });

    this.isVisible$ = this.signInService.setSignInModal$();

    if (this.isGatewaySignIn) {
      this.languageOptions = this.languageSelectorService.getLanguageOptions(
        this.page
      );
      this.signInComponentContent = this.gatewayModalService.cleanGatewaySignIn(
        this.signInComponentContent
      );
      this.showClose = !this.appStateService.isHomePage();
      this.signInComponentContent.bodyContent = this.translateService.instant(
        'signin.gatewaySignInBody'
      );
      this.isVisible$ = this.gatewayModalService.getSignInGatewayModal$();
      this.gatewayModalService.setScrollBodyElement(
        this.documentRef.getElementsByTagName('body')
      );
      this.gatewayModalService.checkSignInGatewayModal();
    }
  }

  /**
   * Set sign-in parameters required for component
   */
  private getSignIn(): SignInInterface {
    return {
      legacy: this.isLegacy,
      signOut: false,
      skin: 'basic',
      showLegacyContent: this.isLegacy,
    };
  }
  /**
   * Set LM legacy for component
   */
  private getLegacy(): boolean {
    // Check Site configuration first
    if (
      this.globalConfigService.config.siteConfiguration?.site
        ?.authenticationLegacy
    ) {
      return this.globalConfigService.config.siteConfiguration.site
        .authenticationLegacy;
    }
    return false;
  }

  public onSubmit(signIn: SignInEvent) {
    logger.debug('SignInEvent', signIn);
    this.signInForm.value.userId = signIn.userName;
    this.signInForm.value.password = signIn.password;
    this.isSubmitButtonDisabled = true;
    const submitObject: AnalyticsLogin = {
      signInForm: this.signInForm,
      analyticsKey: 'Modal',
    };
    this.signInService.onSubmit(submitObject);
  }

  public onSubmitExt() {
    const signInModal: HTMLElement = this.documentRef.getElementById(
      'signInModal'
    );
    const signInInput: HTMLCollectionOf<HTMLInputElement> = signInModal.getElementsByTagName(
      'input'
    );
    const signIn: SignInEvent = {
      userName: signInInput.namedItem('userName').value,
      password: signInInput.namedItem('password').value,
      rememberMe: false,
    };
    if (this.isGatewaySignIn) {
      this.signInForm.value.userId = signIn.userName;
      this.signInForm.value.password = signIn.password;
      this.gatewayModalService.onSubmit(this.signInForm);
      if (this.gatewayModalService.getSignInSuccess()) {
        this.gatewayModalService.hideSignInGatewayModal();
      }
      // Set error label when sign-in fail.
      this.signInComponentContent.errorText = this.translateService.instant(
        'signin.modalSignInErrorLabel'
      );
      return;
    }
    this.onSubmit(signIn);
  }

  public closeModal(): void {
    this.signInService.hideSignInModal();
    if (this.isGatewaySignIn) {
      // Redirecting "simple login" to homepage when modal close.
      const homepage = this.appStateService.relativeHomeURL(
        this.appStateService.getHomePageUrl()
      );
      this.router.navigateByUrl(homepage).then((redirect: boolean) => {
        if (!redirect) {
          logger.debug('Redirect to homepage failed');
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.signInService.hideFavMsgInModal();
  }
}
